import { useEffect, useId, useRef } from 'react';
import { Icon } from './Icon';
import { TypeToConfirmField, useConfirmGate } from './confirm-gate';
import { useFocusTrap } from './useFocusTrap';

/**
 * Stack global de modales abiertos. Resuelve el bug "Escape cierra todos los
 * modales anidados a la vez": múltiples listeners hermanos sobre `window` no
 * pueden detenerse entre sí con stopPropagation. La solución es un único
 * listener en capture phase + un stack de callbacks; solo el modal del top
 * recibe el Escape, y se consume el evento para que nadie más reaccione.
 *
 * Cada modal abierto debe push su `onClose` al stack al montar y removerlo al
 * desmontar (vía useModalEscape hook).
 */
const modalStack: Array<() => void> = [];
let stackInstalled = false;

function installModalStack() {
  if (stackInstalled) return;
  if (typeof window === 'undefined') return;
  stackInstalled = true;
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && modalStack.length > 0) {
        const closeTop = modalStack[modalStack.length - 1];
        // stopImmediatePropagation: detiene también listeners hermanos en el
        // mismo nodo (e.g. cuando target===window). Listeners no migrados al
        // stack (Team.tsx, Suppliers.tsx) en bubble-phase quedan neutralizados.
        e.stopImmediatePropagation();
        e.preventDefault();
        closeTop();
      }
    },
    { capture: true },
  );
}

/**
 * Hook que registra el modal en el stack global mientras esté montado.
 * `closer` se invoca cuando el user pulsa Escape y este modal está en el top.
 * Si `disabled` es true (ej: modal en estado busy), no se registra.
 */
export function useModalEscape(closer: () => void, disabled = false) {
  useEffect(() => {
    if (disabled) return;
    installModalStack();
    modalStack.push(closer);
    return () => {
      const idx = modalStack.lastIndexOf(closer);
      if (idx >= 0) modalStack.splice(idx, 1);
    };
  }, [closer, disabled]);
}

/**
 * Modal de confirmación reusable, en lugar de confirm() nativo del browser.
 * Estilo consistente con el resto de modales del módulo.
 */
export function ConfirmModal({
  title,
  body,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  busy = false,
  confirmCountdownSeconds,
  requireTypeToConfirm,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  // V3.9 — opt-in para hard-delete irreversible: contador hacia atrás (seg) y
  // doble confirmación type-to-confirm (texto exacto a escribir). Sin estos
  // props, el modal se comporta igual que antes (backward-compat total).
  confirmCountdownSeconds?: number;
  requireTypeToConfirm?: string;
  // Disable extra del confirm, INDEPENDIENTE de `busy` (que también deshabilita
  // cancel). Para casos como "tiene referencias, no se puede borrar".
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  useModalEscape(onCancel, busy);
  useFocusTrap(containerRef, !busy);

  const { remaining, typed, setTyped, gateOk } = useConfirmGate({
    countdownSeconds: confirmCountdownSeconds,
    requireText: requireTypeToConfirm,
  });
  // gateOk SOLO alimenta el confirm; cancel/Escape nunca dependen de él.
  const confirmIsDisabled = busy || confirmDisabled || !gateOk;

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onCancel}>
      <div
        className="modal"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onCancel}
            disabled={busy}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body">
          {typeof body === 'string' ? (
            <p style={{ margin: 0, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</p>
          ) : (
            body
          )}
          {requireTypeToConfirm && !confirmDisabled && (
            <TypeToConfirmField
              requireText={requireTypeToConfirm}
              typed={typed}
              onChange={setTyped}
              disabled={busy}
            />
          )}
        </div>
        <div className="modal-foot">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={confirmIsDisabled}
          >
            {busy
              ? 'Procesando…'
              : remaining > 0
                ? `${confirmLabel} (${remaining})`
                : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal de aviso/error simple — solo botón "Aceptar". Reemplaza alert() nativo.
 */
export function AlertModal({
  title,
  body,
  onClose,
  destructive = false,
}: {
  title: string;
  body: string | React.ReactNode;
  onClose: () => void;
  destructive?: boolean;
}) {
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  useModalEscape(onClose);
  useFocusTrap(containerRef, true);
  useEffect(() => {
    // Enter también cierra el AlertModal (atajo "Aceptar" sin mover la mano).
    // Independiente del stack de Escape, porque solo cierra el modal del top
    // si el botón Aceptar tiene foco — pero el autoFocus ya garantiza eso.
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-head">
          <h2 id={titleId} style={destructive ? { color: 'var(--red-fg)' } : undefined}>
            {title}
          </h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Cerrar">
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body">
          {typeof body === 'string' ? (
            <p style={{ margin: 0, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</p>
          ) : (
            body
          )}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-primary" onClick={onClose} autoFocus>
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
}
